// sekcie_v1 (20. 10. 2026, 0.J): DetailRow je jeden, v components/Ui.jsx
// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 3 miest
// ╔══════════════════════════════════════════════════════════════════╗
// ║  _ModuleTemplate.jsx — REFERENČNÁ ŠABLÓNA MODULU MATRIX v2        ║
// ║  Ústava 9.D.1 bod 2 — anti-chyba systém.                          ║
// ╠══════════════════════════════════════════════════════════════════╣
// ║  NOVÝ MODUL VZNIKÁ KÓPIOU TOHTO SÚBORU — NIKDY NIE OD NULY.       ║
// ║  Tým sa ústavné systémové veci NEDAJÚ vynechať.                   ║
// ║                                                                  ║
// ║  POSTUP (ústava 9.D.1):                                          ║
// ║   1. PRE-FLIGHT dokument — schváli Mirko PRED prvým riadkom.      ║
// ║   2. cp _ModuleTemplate.jsx → views/MojModul.jsx                 ║
// ║   3. Vyplň CFG · EDIT_FIELDS · STATUS_FILTERS · polia formulára.  ║
// ║   4. Nahraď TODO značky. Skontroluj DoD checklist (dole).         ║
// ╠══════════════════════════════════════════════════════════════════╣
// ║  ČO JE V ŠABLÓNE ZABUDOVANÉ (ústavné systémové veci):            ║
// ║   • master-detail layout ......................... sek. 56       ║
// ║   • LOCK multi-user (cez useRecordEditor) ........ sek. 53.7      ║
// ║   • DIRTY dialóg pri odchode ..................... sek. 53.1      ║
// ║   • Form → Confirm + DIFF pred uložením .......... 12.0/3 · 53.8  ║
// ║   • audit kto/čo/kedy (HistoryPanel + auto-log) .. 12.0/1         ║
// ║   • fulltext hľadanie v zozname .................. sek. 56        ║
// ║   • SearchableSelect (žiadny plain <select>) ..... sek. 56.11     ║
// ║   • TINT pozadia (žiadne inline farby) ........... sek. 21.15     ║
// ║   • dedup kontrola (409 z backendu) .............. sek. 13        ║
// ║   • klávesnica (mod+N, /) + cheatsheet ........... sek. 54        ║
// ║   • breadcrumb — rieši Layout .................... sek. 53.2      ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { COLORS, TYPO, SPACE, RADIUS, TINT, SK, norm } from '../theme.js'
import {
  PageHeader, MasterDetail, SearchInput, ListRow, ListStatusBar,
  FilterChips, KpiRow, KpiCard, Tabs, EmptyPane, SearchableSelect, DetailRow } from '../components/Ui.jsx'
import { Modal } from '../components/Modal.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import { HistoryPanel } from '../components/HistoryPanel.jsx'
import { useToast } from '../components/Toast.jsx'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { useRecordEditor } from '../hooks/useRecordEditor.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { usePositionMemory } from '../hooks/usePositionMemory.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import SyncBadge from '../components/SyncBadge.jsx'
import RowMenu from '../components/RowMenu.jsx'
import ExportMenu from '../components/ExportMenu.jsx'
import useSortable from '../hooks/useSortable.js'

// ╔═══ KONFIGURÁCIA MODULU — VYPLŇ PRI KOPÍROVANÍ ═══════════════════╗
// DÔLEŽITÉ: resource MUSÍ byť presne cesta API segmentu (napr. 'work-schedule'
// pre /api/work-schedule). Server zapisuje audit pod týmto názvom — ak sa
// nezhoduje, HistoryPanel nenájde históriu (ústava-review 9.D.3).
const CFG = {
  resource:    'plu-catalog',
  apiBase:     '/api/plu-catalog',
  titlePlural: 'Katalógy',
  newLabel:    'Nová PLU položka',
}

// Polia pre DIFF (ústava 53.8) — useRecordEditor ich porovná initial vs. zmenené.
// label = ako sa pole volá v Confirm prehľade. format = voliteľné zobrazenie.
const EDIT_FIELDS = [
  { name: 'plu',  label: 'PLU kód' },   // 53.8.A — chýbalo v DIFF
  { name: 'name',       label: 'Názov' },
  { name: 'unit',       label: 'MJ' },
  { name: 'last_price', label: 'Posledná cena' },
]

// Filter chips (ústava 21.15 — farby gest). key 'all' = bez filtra.
const STATUS_FILTERS = [
  { key: 'all', label: 'Všetky', color: COLORS.muted },
]
const STATUS_LABEL = { active: 'Aktívny', inactive: 'Neaktívny' }

// ══════════════════════════════════════════════════════════════════
// HLAVNÝ KOMPONENT — master-detail stránka (ústava sek. 56)
// ══════════════════════════════════════════════════════════════════
export default function PluCatalogView() {
  const { user } = useAuth()                 // user pre LOCK (53.7)
  const toast = useToast()

  // ── pamäť pozície (ústava 53.3) — obnoví výber / filter / hľadanie / scroll ──
  const pos = usePositionMemory(CFG.resource)
  const listRef = useRef(null)

  const [rows, setRows]       = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState(pos.filter?.search || '')
  const [filter, setFilter]   = useState(pos.filter?.status || 'all')
  const [selectedId, setSelectedId] = useState(pos.selectedRow || null)
  const [modal, setModal]     = useState(null)  // null | {record} | {record:null}

  // ── načítanie dát ────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetchAuth(CFG.apiBase)
      const d = await r.json()
      setRows((Array.isArray(d) ? d : (d.rows || [])).map(r => ({ ...r, id: r.plu })))
    } catch (_) {
      toast.error('Dáta sa nenačítali.')
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])
  useLiveRefresh(load)  // živé obnovenie (SSE, ústava 97)

  // ── filter + fulltext hľadanie (ústava sek. 56) ──────────────────
  const filtered = useMemo(() => {
    const q = norm(search.trim())   // V3: bez diakritiky (66.1)
    return rows.filter(row => {
      if (filter !== 'all' && row.status !== filter) return false
      if (!q) return true
      // hľadá vo všetkých textových poliach záznamu
      return Object.values(row).some(v =>
        v != null && norm(v).includes(q))
    })
  }, [rows, search, filter])
  const sortable = useSortable(filtered)

  const selected = useMemo(
    () => rows.find(r => r.id === selectedId) || null,
    [rows, selectedId])

  // ── pamäť pozície — zápis zmien (ústava 53.3) ────────────────────
  useEffect(() => { pos.selectRow(selectedId) }, [selectedId, pos.selectRow])
  useEffect(() => { pos.setFilter({ status: filter, search }) }, [filter, search, pos.setFilter])
  useEffect(() => {
    if (!loading && listRef.current) pos.restoreScroll(listRef.current)
  }, [loading])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── klávesová navigácia zoznamu (ústava 54.2, 56.5) — zdieľaný hook ──
  const openRecord = useCallback((row) => setModal({ record: row }), [])
  useListKeyboard({
    items: sortable.sorted,   // V3 P1 (N01): rovnaké poradie ako zoznam
    selectedId,
    onSelect: setSelectedId,
    onOpen: openRecord,
    enabled: !modal,
  })

  // ── klávesové skratky (ústava sek. 54) ───────────────────────────
  useShortcuts([
    { keys: 'alt+n', label: CFG.newLabel, scope: CFG.resource,
      handler: () => setModal({ record: null }) },
    { keys: '/', label: 'Hľadať', scope: CFG.resource, handler: () => {
      const el = document.querySelector(`[data-${CFG.resource}-search]`)
      if (el) el.focus()
    } },
  ])

  // ── KPI súhrn (ústava sek. 56.8) ─────────────────────────────────
  const kpi = useMemo(() => ({
    total: rows.length,
    usage: rows.reduce((acc, r) => acc + (r.usage_count || 0), 0),
  }), [rows])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader title={CFG.titlePlural} subtitle={`${filtered.length} z ${rows.length}`}>
        <BtnAdd onClick={() => setModal({ record: null })}>+ {CFG.newLabel}</BtnAdd>
      </PageHeader>

      <KpiRow cols={2}>
        <KpiCard title="Položiek" color={COLORS.nav} value={kpi.total} />
        <KpiCard title="Σ použití" color={COLORS.add} value={kpi.usage} />
      </KpiRow>

      <div style={{ height: SPACE.md }} />

      {/* KATALOGY_TABS — pripravené na viac katalógov (zatiaľ len PLU aktívny) */}
      <Tabs
        value={"plu"}
        onChange={() => {}}
        tabs={[
          { key: 'plu',         label: 'PLU (skladové)' },
          { key: 'dodavatelia', label: 'Dodávateľský', active: false },
          { key: 'vlastny',     label: 'Vlastné položky', active: false },
        ]}
      />

      <MasterDetail
        leftWidth={440}
        left={
          <>
            <SearchInput value={search} onChange={setSearch}
              dataAttr={`data-${CFG.resource}-search`}
              placeholder="Hľadať (stlač /)…" />
            <FilterChips options={STATUS_FILTERS} value={filter} onChange={setFilter} />
            {/* PLU_TABLE_HEADER — tabuľkový layout, sticky header riadok */}
            <div ref={listRef}
              onScroll={e => pos.saveScroll(e.currentTarget.scrollLeft, e.currentTarget.scrollTop)}
              style={{ flex: 1, overflow: 'auto' }}>
              {/* Header riadok */}
              <div style={{
                position: 'sticky', top: 0, zIndex: 2,
                display: 'grid',
                gridTemplateColumns: '60px minmax(0,1fr) 42px 78px 30px',
                gap: 0,
                padding: '8px 12px',
                background: COLORS.bgSoft,
                borderBottom: `2px solid ${COLORS.line}`,
                fontSize: TYPO.sizes.xs,
                fontWeight: TYPO.weights.bold,
                color: COLORS.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}>
                <div onClick={sortable.sortClick('plu')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Klik = zoradiť, Shift+klik = pridať druhý kľúč">
                  PLU {sortable.sortIndicator('plu') && <span style={{ color: COLORS.brand }}>{sortable.sortIndicator('plu')}</span>}
                </div>
                <div onClick={sortable.sortClick('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Názov položky {sortable.sortIndicator('name') && <span style={{ color: COLORS.brand }}>{sortable.sortIndicator('name')}</span>}
                </div>
                <div onClick={sortable.sortClick('unit')} style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}>
                  MJ {sortable.sortIndicator('unit') && <span style={{ color: COLORS.brand }}>{sortable.sortIndicator('unit')}</span>}
                </div>
                <div onClick={sortable.sortClick('last_price')} style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }}>
                  Posl. cena {sortable.sortIndicator('last_price') && <span style={{ color: COLORS.brand }}>{sortable.sortIndicator('last_price')}</span>}
                </div>
                <div></div>
              </div>

              {loading ? (
                <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
                  Načítavam…
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
                  Žiadne záznamy.
                </div>
              ) : sortable.sorted.map((row, idx) => {
                const isSelected = row.id === selectedId
                return (
                  <div key={row.id}
                    data-row-id={row.id}
                    onClick={() => setSelectedId(row.id)}
                    onDoubleClick={() => setModal({ record: row })}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = COLORS.bgSoft }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = idx % 2 ? COLORS.bgSoft : 'transparent' }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '60px minmax(0,1fr) 42px 78px 30px',
                      gap: 0,
                      padding: '6px 12px',
                      borderBottom: `1px solid ${COLORS.line}`,
                      background: isSelected ? COLORS.navBg : (idx % 2 ? COLORS.bgSoft : 'transparent'),
                      borderLeft: isSelected ? `3px solid ${COLORS.brand}` : '3px solid transparent',
                      cursor: 'pointer',
                      fontSize: TYPO.sizes.sm,
                      alignItems: 'center',
                    }}>
                    <div style={{ fontFamily: TYPO.mono, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                      {row.plu}
                    </div>
                    <div style={{
                      fontWeight: isSelected ? TYPO.weights.bold : TYPO.weights.normal,
                      color: COLORS.text,
                      whiteSpace: 'normal', wordBreak: 'break-word',
                    }}>
                      {row.name || '(bez názvu)'}
                    </div>
                    <div style={{ fontFamily: TYPO.mono, fontSize: TYPO.sizes.xs, color: COLORS.muted, textAlign: 'center' }}>
                      {row.unit || 'ks'}
                    </div>
                    <div style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.semibold,
                      color: COLORS.text, textAlign: 'right' }}>
                      {row.last_price != null ? SK.eur(row.last_price) : '—'}
                    </div>
                    <div style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <RowMenu compact actions={[
                        { key: 'edit',  label: 'Upraviť',         onClick: () => setModal({ record: row }) },
                        { key: 'price', label: 'Aktualizovať cenu', onClick: () => alert('Nová cena — pripravujeme') },
                        { key: 'print', label: 'Tlačiť',          onClick: () => alert('Tlač — pripravujeme') },
                        { key: 'pdf',   label: 'Uložiť ako PDF',  onClick: () => alert('PDF — pripravujeme') },
                        { separator: true },
                        { key: 'del',   label: 'Vymazať', danger: true, onClick: () => alert('Vymazanie — potvrď v karte') },
                      ]} />
                    </div>
                  </div>
                )
              })}
            </div>
            <ListStatusBar>
              ↑↓ výber · Enter detail · Alt+N nový · / hľadať
              <span style={{ float: 'right' }}><SyncBadge source="mksoft" /></span>
            </ListStatusBar>
          </>
        }
        right={
          selected
            ? <DetailPane record={selected} onEdit={() => setModal({ record: selected })} />
            : <EmptyPane text="Vyber záznam zo zoznamu vľavo" />
        }
      />

      {modal && (
        <RecordModal
          open
          record={modal.record}
          user={user}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            load()
            toast.success('Uložené.')
          }}
        />
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// STATUS TAG — farebný štítok stavu (ústava 21.15 — z TINT, nie inline)
// ══════════════════════════════════════════════════════════════════
function StatusTag({ status }) {
  const isActive = status === 'active'
  return (
    <span style={{
      fontSize: TYPO.sizes.xs,
      fontWeight: TYPO.weights.semibold,
      color: isActive ? COLORS.add : COLORS.muted,
      background: isActive ? TINT.add : COLORS.lineSoft,
      borderRadius: RADIUS.sm,
      padding: '1px 7px',
    }}>{STATUS_LABEL[status] || status || '—'}</span>
  )
}

// ══════════════════════════════════════════════════════════════════
// DETAIL PANE — pravá strana, taby Detail / História (ústava sek. 56)
// ══════════════════════════════════════════════════════════════════
function DetailPane({ record, onEdit }) {
  const [tab, setTab] = useState('detail')
  return (
    <div style={{
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      height: '100%',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* HLAVIČKA KARTY — tenký stavový pásik + čierny názov (ústava 61.6) */}
      <div style={{ height: 4, background: COLORS.brand }} />
      <div style={{
        display: 'flex', alignItems: 'center', gap: SPACE.md,
        padding: `${SPACE.md}px ${SPACE.lg}px`,
        borderBottom: `1px solid ${COLORS.line}`,
      }}>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra,
          color: COLORS.text }}>
          {record.name || '(bez názvu)'}
        </h2>
        <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{record.usage_count || 0}× použité</span>
        <div style={{ flex: 1 }} />
        <Btn variant="nav" onClick={onEdit}>Upraviť</Btn>
      </div>

      <div style={{ padding: SPACE.md, overflow: 'auto', flex: 1, background: COLORS.bg,
        border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { key: 'detail',  label: 'Detail' },
            { key: 'history', label: 'História' },
          ]}
        />
        {tab === 'detail' ? (
          <div style={{ fontSize: TYPO.sizes.base, lineHeight: 1.7 }}>
            <DetailRow label="PLU kód"        value={record.plu} />
            <DetailRow label="Názov"          value={record.name} />
            <DetailRow label="MJ"             value={record.unit} />
            <DetailRow label="Posledná cena"  value={record.last_price != null ? SK.eur(record.last_price) : ''} />
            <DetailRow label="Priemerná cena" value={record.avg_price != null ? SK.eur(record.avg_price) : ''} />
            <DetailRow label="Počet použití"  value={record.usage_count != null ? String(record.usage_count) : ''} />
            <DetailRow label="Aktualizované"  value={record.updated_at} />
          </div>
        ) : (
          // audit kto/čo/kedy (ústava 12.0 bod 1).
          // POZN: auto-audit zapisuje entity_id len pri PUT/PATCH/DELETE
          // (úpravy). CREATE má entity_id prázdny — kto vytvoril záznam
          // pribudne keď backend dostane logAudit() helper (ROADMAP, 9.D.6).
          <HistoryPanel module={CFG.resource} entityId={record.id} />
        )}
      </div>
    </div>
  )
}


// ══════════════════════════════════════════════════════════════════
// RECORD MODAL — edit / new cez useRecordEditor (S1)
// Lock + dirty + Form→Confirm + DIFF — všetko zabudované v hooku.
// ══════════════════════════════════════════════════════════════════
function RecordModal({ open, record, user, onClose, onSaved }) {
  const isNew = !record

  const ed = useRecordEditor({
    resource: CFG.resource,
    id: record?.id || null,
    user,
    initial: {
      plu:        record?.plu        || '',
      name:       record?.name       || '',
      unit:       record?.unit       || 'ks',
      last_price: record?.last_price != null ? String(record.last_price) : '',
    },
    fields: EDIT_FIELDS,
    onSubmit: async (values) => {
      const r = await fetchAuth(
        isNew ? CFG.apiBase : `${CFG.apiBase}/${record.id}`,
        { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(values) }
      )
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        // dedup (ústava sek. 13) — backend vracia 409 pri duplicite
        if (r.status === 409) throw new Error(d.message || 'Taký záznam už existuje.')
        throw new Error(d.error || 'Uloženie zlyhalo.')
      }
    },
  })

  if (!open) return null

  const doClose = () => ed.requestClose(onClose)

  const validate = (v) => {
    if (!v.plu || !v.plu.trim()) return 'PLU kód je povinný.'
    if (!v.name || !v.name.trim()) return 'Názov je povinný.'
    return null
  }

  const handleConfirm = async () => {
    const ok = await ed.submit()
    if (ok) onSaved()
  }

  const readOnly = !ed.canEdit

  return (
    <>
      <Modal
        open
        onClose={doClose}
        onDirtyClose={doClose}
        isDirty={ed.isDirty}
        title={isNew ? CFG.newLabel : `Upraviť — ${record.name}`}
        subtitle={ed.phase === 'confirm' ? 'Skontroluj zmeny pred uložením' : ''}
        maxWidth={640}
        footer={
          ed.phase === 'form' ? (
            <>
              <BtnGhost onClick={doClose}>Zrušiť</BtnGhost>
              <BtnAdd onClick={() => ed.goConfirm(validate)} disabled={readOnly}>
                Uložiť →
              </BtnAdd>
            </>
          ) : (
            <>
              <BtnGhost onClick={ed.back}>← Späť na úpravu</BtnGhost>
              <BtnAdd onClick={handleConfirm} disabled={ed.submitting}>
                {ed.submitting ? 'Ukladám…' : '✓ Potvrdiť uloženie'}
              </BtnAdd>
            </>
          )
        }
      >
        {/* LOCK banner — záznam upravuje niekto iný (ústava 53.7) */}
        {readOnly && ed.lockedBy && (
          <div style={{
            background: TINT.remove,
            border: `1px solid ${COLORS.remove}`,
            borderRadius: RADIUS.md,
            padding: SPACE.sm,
            marginBottom: SPACE.md,
            fontSize: TYPO.sizes.sm,
            color: COLORS.remove,
          }}>
            Záznam práve upravuje <strong>{ed.lockedBy}</strong>. Editácia je zamknutá.
          </div>
        )}

        {ed.error && (
          <div style={{
            background: TINT.remove,
            border: `1px solid ${COLORS.remove}`,
            borderRadius: RADIUS.md,
            padding: SPACE.sm,
            marginBottom: SPACE.md,
            fontSize: TYPO.sizes.sm,
            color: COLORS.remove,
          }}>{ed.error}</div>
        )}

        {ed.phase === 'form' ? (
          // ── FORM fáza ──────────────────────────────────────────────
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            <Field label="PLU kód *">
              <input type="text" value={ed.values.plu} disabled={readOnly || !isNew}
                onChange={e => ed.set('plu', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Názov *">
              <input type="text" value={ed.values.name} disabled={readOnly}
                onChange={e => ed.set('name', e.target.value)} style={inputStyle} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
              <Field label="MJ">
                <input type="text" value={ed.values.unit} disabled={readOnly}
                  onChange={e => ed.set('unit', e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Posledná cena (€)">
                <input type="number" step="0.01" value={ed.values.last_price} disabled={readOnly}
                  onChange={e => ed.set('last_price', e.target.value)} style={inputStyle} />
              </Field>
            </div>
          </div>
        ) : (
          // ── CONFIRM fáza — DIFF prehľad (ústava 53.8) ───────────────
          <div>
            {isNew ? (
              <p style={{ fontSize: TYPO.sizes.base, color: COLORS.text }}>
                Vytvorí sa nová PLU položka <strong>{ed.values.name}</strong>.
              </p>
            ) : ed.diff.length === 0 ? (
              <p style={{ color: COLORS.muted }}>Žiadne zmeny.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
                <thead>
                  <tr style={{ background: TINT.nav }}>
                    <th style={thStyle}>Pole</th>
                    <th style={thStyle}>Pôvodné</th>
                    <th style={thStyle}>Nové</th>
                  </tr>
                </thead>
                <tbody>
                  {ed.diff.map(d => (
                    <tr key={d.name}>
                      <td style={tdStyle}>{d.label}</td>
                      <td style={{ ...tdStyle, color: COLORS.muted, textDecoration: 'line-through' }}>
                        {d.from}
                      </td>
                      <td style={{ ...tdStyle, color: COLORS.add, fontWeight: TYPO.weights.semibold }}>
                        {d.to}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Modal>

      {/* DIRTY dialóg — Uložiť / Pokračovať / Zahodiť (ústava 53.1) */}
      <Modal.DirtyConfirm {...ed.dirtyProps} />
    </>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
        color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>{label}</div>
      {children}
    </label>
  )
}

const inputStyle = {
  width: '100%',
  padding: '6px 10px',
  fontSize: TYPO.sizes.base,
  fontFamily: 'inherit',
  border: `1px solid ${COLORS.line}`,
  borderRadius: RADIUS.md,
  outline: 'none',
  boxSizing: 'border-box',
}
const thStyle = {
  textAlign: 'left', padding: '6px 10px',
  fontSize: TYPO.sizes.xs, color: COLORS.nav,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: `1px solid ${COLORS.line}`,
}
const tdStyle = {
  padding: '6px 10px',
  borderBottom: `1px solid ${COLORS.lineSoft}`,
}

// ╔══════════════════════════════════════════════════════════════════╗
// ║  DoD CHECKLIST — skopíruj do HANDOFF, odškrtni pred dokončením    ║
// ║  (ústava 9.D.1 bod 3). Modul NIE JE hotový kým nie sú všetky ✅.  ║
// ╠══════════════════════════════════════════════════════════════════╣
// ║  [ ] CFG vyplnené (resource, apiBase, labels)                    ║
// ║  [ ] EDIT_FIELDS = všetky editovateľné polia (kvôli DIFF)        ║
// ║  [ ] LOCK funguje — test 2 okná naraz (53.7)                     ║
// ║  [ ] DIRTY dialóg pri zatvorení s neuloženými zmenami (53.1)     ║
// ║  [ ] Form → Confirm + DIFF pred uložením (12.0/3, 53.8)         ║
// ║  [ ] HistoryPanel ukazuje audit záznamu (12.0/1)                 ║
// ║  [ ] hľadanie v zozname funguje (56)                             ║
// ║  [ ] žiadny plain <select> — len SearchableSelect (56.11)        ║
// ║  [ ] žiadne inline farby pozadia — len TINT/tint() (21.15)       ║
// ║  [ ] dedup — backend vracia 409 pri duplicite (13)               ║
// ║  [ ] mod+N a / skratky + cheatsheet (54)                         ║
// ║  [ ] starý modul porovnaný — všetky polia/funkcie prenesené      ║
// ║  [ ] ÚSTAVA-REVIEW — druhý prechod kódu bod po bode (9.D.3)      ║
// ╚══════════════════════════════════════════════════════════════════╝
